/**
 * 合并 Playground 配置和 Pricing 配置
 * 生成统一的模型/计费列表数据结构
 */

// Pricing 配置分类到 Playground 分类的映射
const CATEGORY_MAPPING = {
  token_models: 'chat',
  image_models: 'image',
  video_models: 'video',
  audio_models: 'audio',
  upscale_models: 'upscale',
};

// 分类元信息
const CATEGORY_META = {
  chat: { name: '文本对话', icon: '💬' },
  decision: { name: '结构化决策', icon: '🧩' },
  image: { name: '图片生成', icon: '🎨' },
  video: { name: '视频生成', icon: '🎬' },
  audio: { name: '音频生成', icon: '🎵' },
  upscale: { name: '超分', icon: '🔍' },
};

/**
 * 根据模型 ID 推断厂商名称
 */
function inferVendor(modelId) {
  const id = modelId.toLowerCase();
  if (id === 'jev-1.13.0' || id === 'laya') return 'UCloud ModelVerse';

  // OpenAI 系列
  if (
    id.startsWith('gpt-') ||
    id.startsWith('o1') ||
    id.startsWith('o3') ||
    id.includes('chatgpt') ||
    id.startsWith('text-embedding')
  ) {
    return 'OpenAI';
  }

  // Anthropic
  if (id.startsWith('claude-')) {
    return 'Anthropic';
  }

  // Google
  if (id.startsWith('gemini-') || id.startsWith('gemma-')) {
    return 'Google';
  }

  // 豆包
  if (id.startsWith('doubao-') || id.startsWith('Doubao-')) {
    return '豆包';
  }

  // 通义千问
  if (id.startsWith('qwen')) {
    return '通义千问';
  }

  // 可灵
  if (id.startsWith('kling-') || id.includes('baidu-kling')) {
    return '可灵';
  }

  // 万相
  if (id.includes('wanx') || id.includes('万相')) {
    return '万相';
  }

  // 文心一言
  if (id.startsWith('ernie-') || id.includes('wenxin')) {
    return '文心一言';
  }

  // 智谱
  if (
    id.startsWith('glm-') ||
    id.startsWith('cogview') ||
    id.startsWith('cogvideox')
  ) {
    return '智谱';
  }

  // Moonshot
  if (id.startsWith('moonshot-')) {
    return 'Moonshot';
  }

  // DeepSeek
  if (id.startsWith('deepseek-')) {
    return 'DeepSeek';
  }

  // MiniMax
  if (id.startsWith('abab') || id.includes('minimax')) {
    return 'MiniMax';
  }

  // 讯飞
  if (id.startsWith('spark') || id.includes('iflytek')) {
    return '讯飞星火';
  }

  // 阿里云
  if (
    id.startsWith('cosyvoice') ||
    id.startsWith('paraformer') ||
    id.includes('sambert') ||
    id.includes('wanx')
  ) {
    return '阿里云';
  }

  // 腾讯
  if (id.startsWith('hunyuan-')) {
    return '腾讯混元';
  }

  // 字节跳动（火山引擎）
  if (id.includes('volcano') || id.includes('bytedance')) {
    return '火山引擎';
  }

  // Stability AI
  if (id.startsWith('stable-') || id.includes('stability')) {
    return 'Stability AI';
  }

  // Midjourney
  if (id.includes('midjourney') || id.includes('mj-')) {
    return 'Midjourney';
  }

  // DALL-E
  if (id.startsWith('dall-e')) {
    return 'OpenAI';
  }

  // Suno
  if (id.startsWith('suno-') || id.includes('chirp')) {
    return 'Suno';
  }

  // Luma
  if (id.startsWith('luma-') || id.includes('dream-machine')) {
    return 'Luma';
  }

  // Runway
  if (id.startsWith('runway-') || id.startsWith('gen-')) {
    return 'Runway';
  }

  // Pika
  if (id.startsWith('pika-')) {
    return 'Pika';
  }

  // Seedream
  if (id.startsWith('seedream-')) {
    return 'Seedream';
  }

  // TopazLabs
  if (id.startsWith('topaz-')) {
    return 'TopazLabs';
  }

  // Sora (OpenAI)
  if (id.startsWith('sora-')) {
    return 'OpenAI';
  }

  return '其他';
}

/**
 * 规范化厂商名称（从 Playground 配置中提取）
 */
function normalizeVendorName(vendor) {
  const vendorMap = {
    openai: 'OpenAI',
    modelverse: 'UCloud ModelVerse',
    anthropic: 'Anthropic',
    google: 'Google',
    ali: '阿里云',
    baidu: '百度',
    doubao: '豆包',
    qwen: '通义千问',
    zhipu: '智谱',
    moonshot: 'Moonshot',
    deepseek: 'DeepSeek',
    minimax: 'MiniMax',
    iflytek: '讯飞星火',
    hunyuan: '腾讯混元',
    stability: 'Stability AI',
    midjourney: 'Midjourney',
    suno: 'Suno',
    luma: 'Luma',
    runway: 'Runway',
    pika: 'Pika',
    kling: '可灵',
    wanx: '万相',
    seedream: 'Seedream',
  };

  const lowerVendor = vendor.toLowerCase();
  return vendorMap[lowerVendor] || vendor;
}

function resolveCreditPricing(pricingData, pricingConfig) {
  if (!pricingData || pricingData.type !== 'credit') {
    return pricingData;
  }

  const provider = pricingData.credit_provider || 'topazlabs';
  const creditBilling = pricingConfig.credit_models?.[provider];
  if (!creditBilling) {
    return pricingData;
  }

  const activePlan = creditBilling.active_plan || '';
  const activePlanPrice =
    activePlan && creditBilling.plans ? creditBilling.plans[activePlan] : null;

  return {
    ...pricingData,
    credit_billing: creditBilling,
    credit_price: activePlanPrice || creditBilling.credit_price,
    credit_plans: creditBilling.plans || {},
    active_plan: activePlan,
  };
}

const TOPAZ_IMAGE_CREDIT_ESTIMATE = {
  summary: '图片超分约 1 credit / 24MP 输出像素，最低 1 credit',
  note: '最终按 Topaz 任务完成后返回的 credits 结算。',
  rows: [
    ['720p', '0.92MP', '1'],
    ['1080p', '2.07MP', '1'],
    ['2K/QHD', '3.69MP', '1'],
    ['4K', '8.29MP', '1'],
    ['24MP', '24MP', '1'],
    ['8K', '33.18MP', '2'],
    ['50MP', '50MP', '3'],
    ['64MP', '64MP', '3'],
    ['100MP', '100MP', '5'],
  ],
};

const TOPAZ_VIDEO_CREDIT_ESTIMATE = {
  summary: '视频超分约 8 credits/分钟 @1080p，约 31 credits/分钟 @4K',
  note: '最终按 Topaz 视频 estimates.cost[0]（lower bound）结算。',
  rows: [
    ['1s', '1', '1', '1'],
    ['5s', '1', '1', '3'],
    ['10s', '1', '2', '6'],
    ['1min', '4', '8', '31'],
    ['5min', '17', '38', '151'],
    ['10min', '34', '76', '302'],
  ],
};

const TOPAZ_IMAGE_UPSCALE_MODELS = new Set([
  'topaz-standard-v2',
  'topaz-high-fidelity-v2',
  'topaz-low-resolution-v2',
  'topaz-art-cgi',
  'topaz-transparent-upscale',
]);

const TOPAZ_VIDEO_UPSCALE_MODELS = new Set([
  'topaz-apollo-v8',
  'topaz-proteus-v4',
]);

function getTopazCreditEstimate(modelId, pricingData) {
  if (!modelId?.startsWith('topaz-') || pricingData?.type !== 'credit') {
    return null;
  }

  const category = pricingData.category || '';
  if (
    category.includes('视频') ||
    TOPAZ_VIDEO_UPSCALE_MODELS.has(modelId)
  ) {
    return TOPAZ_VIDEO_CREDIT_ESTIMATE;
  }

  if (
    category.includes('图片') ||
    TOPAZ_IMAGE_UPSCALE_MODELS.has(modelId)
  ) {
    return TOPAZ_IMAGE_CREDIT_ESTIMATE;
  }

  return null;
}

/**
 * 合并 Playground 配置和 Pricing 配置
 * @param {Object} playgroundConfig - Playground 配置对象
 * @param {Object} pricingConfig - Pricing 配置对象
 * @returns {Object} 合并后的配置对象
 */
export function mergePricingData(playgroundConfig, pricingConfig) {
  if (!playgroundConfig || !pricingConfig) {
    return { categories: [] };
  }

  // 存储所有模型数据，按 categoryId -> vendorId -> models 组织
  const mergedData = {};

  // 第一步：从 Playground 配置中提取模型
  if (
    playgroundConfig.categories &&
    Array.isArray(playgroundConfig.categories)
  ) {
    playgroundConfig.categories.forEach((category) => {
      const categoryId = category.id;

      if (!mergedData[categoryId]) {
        mergedData[categoryId] = {
          id: categoryId,
          name: category.name,
          icon: category.icon,
          vendors: {},
        };
      }

      if (category.vendors && Array.isArray(category.vendors)) {
        category.vendors.forEach((vendor) => {
          const vendorId = normalizeVendorName(vendor.id);

          if (!mergedData[categoryId].vendors[vendorId]) {
            mergedData[categoryId].vendors[vendorId] = {
              id: vendorId,
              name: vendor.name || vendorId,
              models: [],
            };
          }

          if (vendor.models && Array.isArray(vendor.models)) {
            vendor.models.forEach((model) => {
              mergedData[categoryId].vendors[vendorId].models.push({
                id: model.id,
                name: model.name || model.id,
                description: model.description || '',
                docUrl: model.docUrl || model.url || '',
                vendor: model.vendor || vendor.name || '',
                endpoint: model.endpoint || '',
                fromPlayground: true,
                fromPricing: false,
                pricing: null,
              });
            });
          }
        });
      }
    });
  }

  // 第二步：从 Pricing 配置中添加/更新模型
  Object.entries(CATEGORY_MAPPING).forEach(
    ([pricingCategory, playgroundCategory]) => {
      const models = pricingConfig[pricingCategory];

      if (!models || typeof models !== 'object') {
        return;
      }

      // 确保分类存在
      if (!mergedData[playgroundCategory]) {
        mergedData[playgroundCategory] = {
          id: playgroundCategory,
          name: CATEGORY_META[playgroundCategory]?.name || playgroundCategory,
          icon: CATEGORY_META[playgroundCategory]?.icon || '📦',
          vendors: {},
        };
      }

      Object.entries(models).forEach(([modelId, pricingData]) => {
        const targetCategory = modelId === 'jev-1.13.0' || modelId === 'laya'
          ? 'decision'
          : pricingData.type === 'image_tokens' ? 'image' : playgroundCategory;
        if (!mergedData[targetCategory]) {
          mergedData[targetCategory] = { id: targetCategory, ...CATEGORY_META[targetCategory], vendors: {} };
        }

        const resolvedPricingData = resolveCreditPricing(
          pricingData,
          pricingConfig
        );
        const creditEstimate = getTopazCreditEstimate(
          modelId,
          resolvedPricingData
        );
        const pricingWithEstimate = creditEstimate
          ? { ...resolvedPricingData, credit_estimate: creditEstimate }
          : resolvedPricingData;
        // 推断厂商
        const vendorId = inferVendor(modelId);

        // 确保厂商存在
        if (!mergedData[targetCategory].vendors[vendorId]) {
          mergedData[targetCategory].vendors[vendorId] = {
            id: vendorId,
            name: vendorId,
            models: [],
          };
        }

        // 查找是否已存在该模型（从 Playground 配置中）
        const existingModel = mergedData[targetCategory].vendors[
          vendorId
        ].models.find((m) => m.id === modelId);

        if (existingModel) {
          // 更新现有模型的计费信息
          existingModel.fromPricing = true;
          existingModel.pricing = pricingWithEstimate;
          if (pricingWithEstimate.description && !existingModel.description) {
            existingModel.description = pricingWithEstimate.description;
          }
          if (pricingWithEstimate.url && !existingModel.docUrl) {
            existingModel.docUrl = pricingWithEstimate.url;
          }
          if (pricingWithEstimate.category && !existingModel.category) {
            existingModel.category = pricingWithEstimate.category;
          }
          if (pricingWithEstimate.vendor && !existingModel.vendor) {
            existingModel.vendor = pricingWithEstimate.vendor;
          }
        } else {
          // 添加新模型（仅在 Pricing 中存在）
          mergedData[targetCategory].vendors[vendorId].models.push({
            id: modelId,
            name: pricingWithEstimate.model || modelId,
            description: pricingWithEstimate.description || '',
            docUrl: pricingWithEstimate.url || '',
            category: pricingWithEstimate.category || '',
            vendor: pricingWithEstimate.vendor || vendorId,
            endpoint: '',
            fromPlayground: false,
            fromPricing: true,
            pricing: pricingWithEstimate,
          });
        }
      });
    }
  );

  // 第三步：转换为数组格式并排序
  const categories = Object.values(mergedData).map((category) => ({
    ...category,
    vendors: Object.values(category.vendors)
      .filter((vendor) => vendor.models.length > 0)
      .map((vendor) => ({
        ...vendor,
        models: vendor.models.sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  }));

  // 按照预定义顺序排序分类
  const categoryOrder = ['chat', 'decision', 'image', 'video', 'audio', 'upscale'];
  categories.sort((a, b) => {
    const indexA = categoryOrder.indexOf(a.id);
    const indexB = categoryOrder.indexOf(b.id);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  return { categories };
}

/**
 * 格式化价格
 * @param {Object} price - 价格对象 {usd, cny}
 * @returns {string} 格式化后的价格字符串
 */
export function formatPrice(price) {
  if (!price) return 'N/A';

  if (price.usd !== undefined && price.usd !== null) {
    return `$${price.usd}`;
  }
  if (price.cny !== undefined && price.cny !== null) {
    return `¥${price.cny}`;
  }

  return 'N/A';
}

/**
 * 获取计费类型的显示名称
 */
export function getPricingTypeName(type) {
  const typeNames = {
    image_tokens: '图片 Token 明细定价',
    simple: '简单定价',
    conditional: '阶梯定价',
    matrix: '矩阵定价',
    multiplier: '倍率定价',
    per_char: '按字符计费',
    per_second: '按秒计费',
    per_request: '按请求计费',
    credit: '官方 Credit 计费',
  };

  return typeNames[type] || type;
}
